/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

export interface WorkflowTemplate {
  id: number
  name: string
  description: string
  approvers: any
  collaborators: any
  initialApprovers: any
  rejectionReviewers: any
}

export interface ExportReportRequest {
  filter: any
  meta: {
    sortBy: string
    sortDesc: boolean
  }
}

export interface ExportReportResponse {
  success: boolean
  message?: string
}

class ChangeRequestTypesService {
  private baseURL = 'api/v1/workflowtemplates'
  private baseURL_v2 = 'api/v2/workflowtemplates'

  async getAllForTable(params?: any): Promise<any> {
    const queryParams = params || { sortBy: 'NAME', sortDesc: false }
    return baseApiService.fetch(`${this.baseURL_v2}`, { params: queryParams })
  }

  async getById(typeId: string | number, useVersion1?: boolean): Promise<WorkflowTemplate> {
    const url = useVersion1 ? this.baseURL : this.baseURL_v2
    return baseApiService.fetch<WorkflowTemplate>(`${url}/${typeId}`)
  }

  async getOptionsForAsyncPicker(search?: string, pageStart?: number): Promise<any> {
    const params = {
      name: search || null,
      pageStart: pageStart || 0,
      pageSize: 25
    }

    return baseApiService.fetch(`${this.baseURL_v2}/picker`, { params })
  }

  async getAllForPicker(params?: any): Promise<any> {
    return baseApiService.fetch(`${this.baseURL}/forpicker`, { params })
  }

  async exportReports(filter: any, meta: any): Promise<ExportReportResponse> {
    const reqObj = {
      filter,
      meta
    }

    return baseApiService.fetch<ExportReportResponse>('/api/reports/changerequesttypes/export', {
      method: 'POST',
      body: reqObj
    })
  }

  async create(data: any, transformedData?: boolean): Promise<WorkflowTemplate> {
    const payload = transformedData ? data : this.assembleForServer(data)
    return baseApiService.fetch<WorkflowTemplate>(this.baseURL, {
      method: 'POST',
      body: payload
    })
  }

  async update(id: string | number, data: any, transformedData?: boolean): Promise<WorkflowTemplate> {
    const payload = transformedData ? data : this.assembleForServer(data)
    return baseApiService.fetch<WorkflowTemplate>(`${this.baseURL}/${id}`, {
      method: 'PUT',
      body: payload
    })
  }

  async remove(id: string | number): Promise<void> {
    return baseApiService.fetch<void>(`${this.baseURL}/${id}`, {
      method: 'DELETE'
    })
  }

  async saveAsCRType(fsiId: string | number, type: any): Promise<WorkflowTemplate> {
    const payload = { ...type, fsiId }
    return baseApiService.fetch<WorkflowTemplate>(`/${this.baseURL}/createfromfsi`, {
      method: 'POST',
      body: payload
    })
  }

  private assembleForServer(data: any): any {
    return {
      name: data.name,
      description: data.description,
      approvers: data.approvers || {},
      collaborators: data.collaborators || {},
      initialApprovers: data.initialApprovers || {},
      rejectionReviewers: data.rejectionReviewers || {}
      // Add other transformation logic as needed
    }
  }
}

export const changeRequestTypesService = new ChangeRequestTypesService()
